import type { useErrorToast } from "@editor/hooks/useErrorToast";
import type {
  Product,
  ProductFormSchema,
  ProductMediaItem,
} from "schemas/generated/product";

import { isEmpty } from "replo-utils/lib/misc";
import { MAX_FILE_UPLOAD_COUNT } from "schemas/asset";

interface FileRejection {
  errors: Array<{ code: string; message: string }>;
}

export type FileRejections = Array<FileRejection>;

/**
 * Converts a product object to the format expected by the product form.
 */
export const getProductFormData = (product: Product): ProductFormSchema => {
  // NOTE (Kurt, 2025-09-02): We only use the first variant for now because we only
  // support one variant per product.
  const firstVariant = product.variants[0];

  // Convert product media to ProductMediaItem format
  const productMedia: ProductMediaItem[] =
    product.media
      ?.map((item, index) => ({
        assetId: item.id,
        url: item.type === "image" ? item.url : item.previewImage?.url ?? "",
        sortOrder: item.sortOrder ?? index,
        type: item.type === "image" ? ("image" as const) : ("video" as const),
      }))
      .filter((item) => item.url) ?? [];

  // Convert product files to form format
  const productFiles =
    product.productFiles?.map(
      ({ id, url, name, sizeBytes, fileExtension, label }) => ({
        id,
        url,
        name,
        sizeBytes,
        fileExtension,
        label: label ?? undefined,
      }),
    ) ?? [];

  // Convert productEmail to form format (transform null to undefined)
  const productEmail = product.productEmails?.[0]
    ? {
        header: product.productEmails[0].header,
        message: product.productEmails[0].message ?? undefined,
        buttonText: product.productEmails[0].buttonText ?? undefined,
        buttonLink: product.productEmails[0].buttonLink ?? undefined,
      }
    : undefined;

  return {
    name: product.title,
    productType: product.type ?? "UNASSIGNED",
    price: firstVariant?.price ?? 0,
    compareAtPrice: firstVariant?.originalPrice ?? null,
    description: product.description,
    checkoutMethod:
      product.externalCheckoutUrl && product.externalCheckoutUrl.length > 0
        ? "external"
        : "replo",
    externalCheckoutUrl: product.externalCheckoutUrl ?? null,
    images: [],
    productFiles,
    media: productMedia,
    productEmail,
    hasFulfillmentEmail: Boolean(product.productEmails?.[0]),
  };
};

export const getColorClasses = (color: string) => {
  const colorMap = {
    pink: {
      border: "border-pink-600",
      background: "bg-pink-50",
      iconColor: "text-pink-600",
      iconBg: "bg-pink-50",
      iconBorder: "border-pink-200",
    },
    violet: {
      border: "border-violet-600",
      background: "bg-violet-50",
      iconColor: "text-violet-600",
      iconBg: "bg-violet-50",
      iconBorder: "border-violet-200",
    },
    lime: {
      border: "border-lime-600",
      background: "bg-lime-50",
      iconColor: "text-lime-600",
      iconBg: "bg-lime-50",
      iconBorder: "border-lime-300",
    },
    orange: {
      border: "border-orange-600",
      background: "bg-orange-50",
      iconColor: "text-orange-600",
      iconBg: "bg-orange-50",
      iconBorder: "border-orange-300",
    },
    teal: {
      border: "border-teal-600",
      background: "bg-teal-50",
      iconColor: "text-teal-600",
      iconBg: "bg-teal-50",
      iconBorder: "border-teal-200",
    },
    sky: {
      border: "border-sky-600",
      background: "bg-sky-50",
      iconColor: "text-sky-600",
      iconBg: "bg-sky-50",
      iconBorder: "border-sky-200",
    },
    fuchsia: {
      border: "border-fuchsia-600",
      background: "bg-fuchsia-50",
      iconColor: "text-fuchsia-600",
      iconBg: "bg-fuchsia-50",
      iconBorder: "border-fuchsia-200",
    },
    yellow: {
      border: "border-yellow-600",
      background: "bg-yellow-50",
      iconColor: "text-yellow-600",
      iconBg: "bg-yellow-50",
      iconBorder: "border-yellow-200",
    },
    zinc: {
      border: "border-zinc-600",
      background: "bg-zinc-50",
      iconColor: "text-zinc-600",
      iconBg: "bg-zinc-50",
      iconBorder: "border-zinc-300",
    },
  };
  return colorMap[color as keyof typeof colorMap] ?? colorMap.zinc;
};

/**
 * Truncates a string in the middle, preserving the file extension if present.
 * Useful for displaying long file names in a compact format.
 */
export const truncateMiddle = (
  text: string,
  maxLength: number = 40,
): string => {
  if (text.length <= maxLength) {
    return text;
  }

  const lastDotIndex = text.lastIndexOf(".");
  const extension =
    lastDotIndex !== -1 ? text.slice(Math.max(0, lastDotIndex)) : "";
  const nameWithoutExtension =
    lastDotIndex !== -1 ? text.slice(0, Math.max(0, lastDotIndex)) : text;

  const availableLength = maxLength - extension.length - 3;

  if (availableLength <= 0) {
    return `...${extension}`;
  }

  const startLength = Math.ceil(availableLength / 2);
  const endLength = Math.floor(availableLength / 2);

  const start = nameWithoutExtension.slice(0, Math.max(0, startLength));
  const end = nameWithoutExtension.slice(
    Math.max(0, nameWithoutExtension.length - endLength),
  );

  return `${start}...${end}${extension}`;
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) {
    return "0 Bytes";
  }
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
};

export function handleFileRejections(
  fileRejections: FileRejections,
  errorToast: ReturnType<typeof useErrorToast>,
): boolean {
  if (isEmpty(fileRejections)) {
    return false;
  }

  if (
    fileRejections.find((fileRejection) =>
      fileRejection.errors.find((error) => error.code === "too-many-files"),
    )
  ) {
    errorToast(
      "Files Exceeding Limit",
      `Too many files selected. Please upload up to ${MAX_FILE_UPLOAD_COUNT} files at a time for a successful upload.`,
      "error.asset.upload.too_many_files",
    );
    return true;
  }

  const fileInvalidTypeError = fileRejections
    .flatMap((rejection) => rejection.errors)
    .find((error) => error.code === "file-invalid-type");

  if (fileInvalidTypeError) {
    errorToast(
      "Invalid File Type",
      `${fileInvalidTypeError.message}`,
      "error.asset.upload.invalid_file_type",
    );
  }

  const fileTooLargeError = fileRejections
    .flatMap((rejection) => rejection.errors)
    .find(
      (error) =>
        error.code === "file-too-large" ||
        error.code === "replo-file-too-large",
    );

  if (fileTooLargeError) {
    errorToast(
      "File Too Large",
      fileTooLargeError.code === "file-too-large"
        ? "File size exceeds the 5GB limit. Please choose a smaller file."
        : fileTooLargeError.message,
      "error.asset.upload.file_too_large",
    );
  }

  return false;
}
